import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

const FAV_KEY = "cm.explorer.favorites";
const RECENT_KEY = "cm.explorer.recent";
const PRESET_KEY = "cm.explorer.preset";
const TIME_KEY = "cm.explorer.timeTravel";

export interface ExplorerContextValue {
  favorites: Set<string>;
  recent: Array<{ id: string; name: string; path: string; at: string }>;
  preset: string;
  timeTravelAt: string | null;
  toggleFavorite: (id: string) => void;
  pushRecent: (item: { id: string; name: string; path: string }) => void;
  setPreset: (p: string) => void;
  setTimeTravelAt: (at: string | null) => void;
}

const ExplorerContext = createContext<ExplorerContextValue | null>(null);

function loadSet(key: string): Set<string> {
  try {
    const raw = localStorage.getItem(key);
    return new Set(raw ? JSON.parse(raw) as string[] : []);
  } catch { return new Set(); }
}

function loadRecent(): ExplorerContextValue["recent"] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    return raw ? JSON.parse(raw) as ExplorerContextValue["recent"] : [];
  } catch { return []; }
}

export function ExplorerProvider({ children }: { children: ReactNode }) {
  const [favorites, setFavorites] = useState(() => loadSet(FAV_KEY));
  const [recent, setRecent] = useState(loadRecent);
  const [preset, setPresetState] = useState(() => localStorage.getItem(PRESET_KEY) ?? "client_based");
  const [timeTravelAt, setTimeTravelState] = useState<string | null>(() => localStorage.getItem(TIME_KEY));

  const toggleFavorite = useCallback((id: string) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      localStorage.setItem(FAV_KEY, JSON.stringify([...next]));
      return next;
    });
  }, []);

  const pushRecent = useCallback((item: { id: string; name: string; path: string }) => {
    setRecent((prev) => {
      const next = [{ ...item, at: new Date().toISOString() }, ...prev.filter((r) => r.id !== item.id)].slice(0, 20);
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const setPreset = useCallback((p: string) => {
    setPresetState(p);
    localStorage.setItem(PRESET_KEY, p);
  }, []);

  const setTimeTravelAt = useCallback((at: string | null) => {
    setTimeTravelState(at);
    if (at) localStorage.setItem(TIME_KEY, at);
    else localStorage.removeItem(TIME_KEY);
  }, []);

  const value = useMemo(() => ({
    favorites, recent, preset, timeTravelAt,
    toggleFavorite, pushRecent, setPreset, setTimeTravelAt,
  }), [favorites, recent, preset, timeTravelAt, toggleFavorite, pushRecent, setPreset, setTimeTravelAt]);

  return <ExplorerContext.Provider value={value}>{children}</ExplorerContext.Provider>;
}

export function useExplorer() {
  const ctx = useContext(ExplorerContext);
  if (!ctx) throw new Error("useExplorer must be used within ExplorerProvider");
  return ctx;
}
